import React from 'react';
import { Box, Container, Typography, Button, Stack, Divider } from '@mui/material';
import { ArrowForward } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { brandColors } from '../../theme';

interface HeroSectionProps {
  showExploreButton?: boolean;
  showSampleButton?: boolean;
  align?: 'center' | 'top';
  textAlign?: 'left' | 'center';
  showDivider?: boolean;
  showSubheading?: boolean;
}

export const HeroSection: React.FC<HeroSectionProps> = ({
  showExploreButton = true,
  showSampleButton = true,
  align = 'center',
  textAlign = 'center',
  showDivider = true,
  showSubheading = true,
}) => {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        height: align === 'center' ? 600 : 'auto',
        background: `linear-gradient(to bottom, ${brandColors.white}, ${brandColors.lightGray})`,
        display: 'flex',
        alignItems: align === 'center' ? 'center' : 'flex-start',
        position: 'relative',
      }}
    >
      <Container maxWidth="lg" sx={{ pt: align === 'top' ? 8 : 0 }}>
        <Box sx={{ textAlign: textAlign, maxWidth: 800, mx: textAlign === 'center' ? 'auto' : 0 }}>
          <Typography
            component="h1"
            sx={{
              color: brandColors.blue,
              fontWeight: 400,
              fontSize: { xs: '2.25rem', sm: '2.75rem', md: '3.25rem' },
              lineHeight: 1.05,
              letterSpacing: '-0.5px',
              mb: 2,
            }}
          >
            MarketCompass Lodging Reports
          </Typography>
          
          {showDivider && (
            <Divider
              sx={{
                width: 240,
                mx: textAlign === 'center' ? 'auto' : 0,
                mb: 4,
                backgroundColor: brandColors.gold,
                height: 2,
              }}
            />
          )}
          
          {showSubheading && (
            <>
              <Typography
                variant="h4"
                sx={{
                  color: brandColors.indigo,
                  fontWeight: 500,
                  fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                  mb: 1,
                }}
              >
                Your Gateway to Premium
              </Typography>
              
              <Typography
                variant="h4"
                sx={{
                  color: brandColors.indigo,
                  fontWeight: 500,
                  fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                  mb: 4,
                }}
              >
                Hospitality Market Intelligence
              </Typography>
            </>
          )}
          
          <Typography
            variant="body1"
            sx={{
              color: brandColors.mediumGray,
              fontSize: { xs: '1rem', sm: '1.125rem' },
              lineHeight: 1.8,
              mb: 5,
              px: { xs: 2, sm: 4, md: 0 },
            }}
          >
            The Horwath HTL MarketCompass Lodging Reports are produced monthly and provide in-depth analysis for investors and economists on the lodging market. The reports begin with a detailed overview of the market area, focusing on economic characteristics, followed by an analysis of each of the six hotel classes: Luxury, Upper Upscale, Upscale, Upper Midscale, Midscale, and Economy.
          </Typography>
          
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={3}
            sx={{ justifyContent: 'center', alignItems: 'center' }}
          >
            {showExploreButton && (
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForward />}
                onClick={() => navigate('/global-market-reports')}
                sx={{
                  backgroundColor: brandColors.gold,
                  color: brandColors.white,
                  px: 4,
                  py: 1.5,
                  fontSize: '1.125rem',
                  fontWeight: 600,
                  borderRadius: 2,
                  textTransform: 'none',
                  boxShadow: '0 4px 12px rgba(245, 168, 0, 0.3)',
                  '&:hover': {
                    backgroundColor: '#E09600',
                    transform: 'scale(1.02)',
                    boxShadow: '0 6px 16px rgba(245, 168, 0, 0.4)',
                  },
                  transition: 'all 0.3s ease',
                  minWidth: { xs: 200, sm: 'auto' },
                }}
              >
                Explore Markets
              </Button>
            )}

            {showSampleButton && (
              <Button
                variant="outlined"
                size="large"
                onClick={() => window.open('/sample_reports/2Q 2025 MarketCompass Lodging Report - Colorado Springs, CO.pdf', '_blank')}
                sx={{
                  borderColor: brandColors.blue,
                  color: brandColors.blue,
                  px: 4,
                  py: 1.5,
                  fontSize: '1.125rem',
                  fontWeight: 600,
                  borderRadius: 2,
                  textTransform: 'none',
                  borderWidth: 2,
                  '&:hover': {
                    borderColor: brandColors.blue,
                    backgroundColor: 'rgba(32, 76, 229, 0.05)',
                    borderWidth: 2,
                    transform: 'scale(1.02)',
                  },
                  transition: 'all 0.3s ease',
                  minWidth: { xs: 200, sm: 'auto' },
                }}
              >
                View Sample Report
              </Button>
            )}
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};